import * as XLSX from 'xlsx'
import {STATUS, DISPUTE_TYPE} from './data/reference'
import {EXCESS_NOTE, dimsText} from './format'
import {governingType} from './state/query'
import {evidenceScore, EVIDENCE_LEVEL_LABEL} from './state/evidenceScore'

/**
 * Weight Discrepancy → .xlsx.
 *
 * Built on `xlsx`, the same library the panel's own export helper uses
 * (appointments/shared/exportHelpers.js), and written the same way — one
 * `XLSX.utils.json_to_sheet` per sheet, then `XLSX.writeFile`. No new
 * dependency, and no hand-rolled CSV: a merchant opening this in Excel has
 * to be able to sum a column, which a CSV of pre-formatted strings cannot
 * do.
 *
 * The one deliberate departure from that helper is `cellDates: true`. Its
 * appointment exports carry dates as text; here the deadline and the date
 * raised are dates a merchant will sort and filter on, so they are written
 * as real date cells (type 'd') rather than strings that only look like
 * dates.
 */

const SHEET_NAME = 'Weight discrepancies'
const NOTES_SHEET_NAME = 'About this export'

// Every date column reads the same way in the sheet as it does on screen.
// Excel's own token vocabulary, not date-fns'.
const DATE_FMT = 'dd-mmm-yyyy'
const KG_FMT = '0.00'
// No ₹ glyph in the cell format: the symbol is in the header, so the value
// stays a bare number that sums, sorts and charts. Writing "₹136" would make
// it text — the single most common way an export becomes unusable.
//
// Two decimals because this is money and a real rate card has paise in it.
// The current figures are whole-rupee slab multiples, so the decimals show as
// .00 today — but a column that silently truncates the moment the amounts stop
// being round is the wrong default for a file someone reconciles against an
// invoice. It matches the app's own two-decimal money convention
// (helpers/UtilityHelper.js parseAmount).
const MONEY_FMT = '#,##0.00'

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function asDate(v) {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Blank, not "0×0×0", when nothing was captured — a spreadsheet column people filter and sum on. */
function cartonOf(dims) {
  const text = dimsText(dims)
  return text ? text.replace(' cm', '') : ''
}

/**
 * `updatedAt` is a last-touched stamp on every row, so it only means "resolved
 * on" for a row that has actually reached a resolved status — on a NEW or
 * in-dispute row it is just the last time something moved. Gated on the same
 * `STATUS[...].group === 'resolved'` test state/query.js's avgResolutionDays
 * uses, so the column and the Avg Resolution KPI can never disagree about
 * which rows have a resolution date. Null (an empty cell) everywhere else —
 * never today's date, and never a stamp that would drag an unresolved row
 * into a date-sorted range.
 */
function resolvedDateOf(row) {
  const group = (STATUS[row.status] || {}).group
  return group === 'resolved' ? asDate(row.updatedAt) : null
}

/**
 * The sheet's columns, in order.
 *
 * `header` reuses the grid's own wording wherever the grid has any —
 * "Excess weight", "Excess freight", "Evidence", "Status", "Deadline", and
 * the "Dead Wt." / "Vol Wt." pair the Declared and Billed cells print — so
 * a merchant can map a column back to the screen it came from. Columns the
 * grid folds into a composite cell ("Courier & AWB" is three lines; the
 * carton sits under the weights) are split out under the same words the
 * composite uses, because a spreadsheet cell cannot be three lines.
 *
 * `fmt` is the Excel number format applied to the column; a column with
 * none is left as text or as a plain number, whichever `value` returns.
 *
 * Exported so a test can assert the sheet's headers against the table's
 * headers rather than against a copy of this list.
 */
export const EXPORT_COLUMNS = [
  {header: 'Courier', value: (r) => r.courier || ''},
  {header: 'AWB', value: (r) => r.awb || ''},
  {header: 'Service', value: (r) => r.service || ''},
  {header: 'Order ID', value: (r) => r.orderId || ''},
  {header: 'SKU', value: (r) => r.sku || ''},
  {header: 'Product', value: (r) => r.product || ''},

  {header: 'Declared Wt. (kg)', value: (r) => num(r.originalWeight), fmt: KG_FMT},
  {header: 'Declared Dead Wt. (kg)', value: (r) => num(r.originalWeight), fmt: KG_FMT},
  {header: 'Declared Vol Wt. (kg)', value: (r) => num(r.declaredVolKg), fmt: KG_FMT},
  {header: 'Declared carton (cm)', value: (r) => cartonOf(r.declaredDims)},

  {header: 'Billed Wt. (kg)', value: (r) => num(r.appliedWeight), fmt: KG_FMT},
  {header: 'Billed Dead Wt. (kg)', value: (r) => num(r.deadKg), fmt: KG_FMT},
  {header: 'Billed Vol Wt. (kg)', value: (r) => num(r.volKg), fmt: KG_FMT},
  {header: 'Billed carton (cm)', value: (r) => cartonOf(r.dims)},

  // Which of dead/volumetric governed the charge — on screen this is carried
  // only by bold-vs-muted styling inside the Billed cell, which cannot
  // survive into a spreadsheet, so it is said in words here. Derived through
  // governingType(), never row.type, for the reason that function documents.
  {
    header: 'Governing weight',
    value: (r) => (DISPUTE_TYPE[governingType(r)] || {}).label || '',
  },

  {header: 'Excess weight (kg)', value: (r) => num(r.excessKg), fmt: KG_FMT},
  {header: 'Excess freight (₹)', value: (r) => num(r.excessAmount), fmt: MONEY_FMT},
  // 28 Oct (business): what the merchant was finally billed on and charged — see finalOutcome().
  {header: 'Final billable weight (kg)', value: (r) => num(finalOutcome(r).kg), fmt: KG_FMT},
  {header: 'Final excess amount charged (₹)', value: (r) => num(finalOutcome(r).charge), fmt: MONEY_FMT},

  {header: 'Evidence', value: (r) => EVIDENCE_LEVEL_LABEL[evidenceScore(r).level] || ''},
  {header: 'Status', value: (r) => (STATUS[r.status] || {}).label || r.status || ''},

  {header: 'Deadline', value: (r) => asDate(r.deadlineAt), fmt: DATE_FMT},
  {header: 'Date raised', value: (r) => asDate(r.createdAt), fmt: DATE_FMT},
  // Download is offered on the Resolved tab, where "when did this close?" is
  // the whole question, and the app already trusts this stamp enough to
  // compute the Avg Resolution KPI off it. Sits with the other two dates so
  // the three are one block a merchant can select and sort together.
  {header: 'Date resolved', value: resolvedDateOf, fmt: DATE_FMT},
]

export const EXPORT_HEADERS = EXPORT_COLUMNS.map((c) => c.header)

// The merchant paid on these: accepted, auto-accepted (either spelling of it), or the dispute was rejected.
const MERCHANT_PAID = new Set(['ACCEPTED_BY_MERCHANT', 'AUTO_ACCEPTED', 'AUTO_ACCEPTED_BY_SYSTEM', 'REJECTED_PROSHIP'])
// The merchant was not charged: Prozo or the courier accepted the dispute.
const MERCHANT_WON = new Set(['ACCEPTED_BY_COURIER', 'ACCEPTED_PROSHIP'])

/**
 * The weight the merchant was finally billed on and the excess finally charged. Settled at a revised weight:
 * that weight and its charge. Merchant paid: the courier's billed weight and the excess. Merchant won: the
 * declared weight and nothing. Still open: no outcome yet, so both stay empty.
 */
export function finalOutcome(r) {
  if (!r) return {kg: null, charge: null}
  if (r.status === 'SETTLED_PROSHIP') return {kg: r.settledKg, charge: r.settledCharge}
  if (MERCHANT_PAID.has(r.status)) return {kg: r.appliedWeight, charge: r.excessAmount}
  if (MERCHANT_WON.has(r.status)) return {kg: r.originalWeight, charge: 0}
  return {kg: null, charge: null}
}

/**
 * One flat record per discrepancy, keyed by the column headers above.
 * Values are the real types — Number for the weights and the money, Date for
 * the three dates — not display strings. Pure, so the row count and the cell
 * types can be asserted without writing a file.
 */
export function buildDisputeRows(rows) {
  return (rows || []).map((row) => {
    const rec = {}
    EXPORT_COLUMNS.forEach((col) => { rec[col.header] = col.value(row) })
    return rec
  })
}

// Column widths, so the sheet opens readable instead of as twenty columns of
// ####. Sized off the header plus the widest value each column actually
// carries in this dataset.
const COL_WIDTH = {
  Product: 34,
  SKU: 22,
  'Order ID': 16,
  AWB: 16,
  Status: 22,
  Evidence: 14,
  'Governing weight': 20,
}
const DEFAULT_COL_WIDTH = 13

export const EXCESS_FREIGHT_HEADER = 'Excess freight (₹)'

/**
 * The excess-freight caveat, carried into the file two ways.
 *
 * On screen EXCESS_NOTE (format.js) sits beside every excess figure. The
 * workbook is the one artefact that leaves the app — it gets forwarded to a
 * courier, pasted into a recovery claim, argued over weeks later by someone
 * who never saw the screen — so it is the place the caveat matters most, and
 * it was the only place missing it.
 *
 * It is NOT a text row above the header. A stray row there would push the
 * header off row 1 and break sorting, filtering and every SUM over the money
 * column — the caveat cannot be allowed to damage the data it qualifies.
 * Instead:
 *
 * 1. A cell comment on the "Excess freight (₹)" HEADER. This is the caveat at
 *    the point of use: Excel marks the cell and shows the note on hover, it
 *    travels with the column, and a comment occupies no cell of its own so
 *    nothing about the grid changes.
 * 2. A second sheet, "About this export", spelling the same sentence out as
 *    prose. Cell comments are the fragile half — Google Sheets renders them
 *    differently, a copy-paste of the range drops them, a save-as-CSV loses
 *    them entirely. A sheet survives all of that and is where a reader who
 *    wants to know what they are holding will look.
 *
 * Workbook properties were the third option and were rejected: File → Info is
 * a place nobody looks, which fails "cannot be missed".
 */
function attachExcessCaveat(ws) {
  const c = EXPORT_HEADERS.indexOf(EXCESS_FREIGHT_HEADER)
  if (c < 0) return
  const header = ws[XLSX.utils.encode_cell({r: 0, c})]
  if (!header) return
  header.c = [{a: 'Proship', t: EXCESS_NOTE}]
  // Hover marker, not an open box. Stated rather than left to the writer's
  // default so the choice is visible: `false` makes Excel draw a fixed
  // 104x64pt note floating over the data on every open until dismissed, which
  // buys attention at the cost of covering the first rows every single time.
  // The "About this export" sheet carries the same caveat unmissably, and
  // survives copy-paste and save-as-CSV where a comment does not.
  header.c.hidden = true
}

function buildNotesSheet() {
  const ws = XLSX.utils.aoa_to_sheet([
    ['About this export'],
    [],
    [EXCESS_FREIGHT_HEADER, EXCESS_NOTE],
  ])
  ws['!cols'] = [{wch: 22}, {wch: 96}]
  return ws
}

export function buildDisputeWorkbook(rows) {
  const records = buildDisputeRows(rows)
  const ws = XLSX.utils.json_to_sheet(records, {header: EXPORT_HEADERS, cellDates: true})

  // json_to_sheet types the cells but has no opinion on how they display, so
  // the per-column format is stamped on afterwards. Row 0 is the header.
  EXPORT_COLUMNS.forEach((col, c) => {
    if (!col.fmt) return
    for (let r = 1; r <= records.length; r += 1) {
      const cell = ws[XLSX.utils.encode_cell({r, c})]
      if (cell) cell.z = col.fmt
    }
  })

  ws['!cols'] = EXPORT_COLUMNS.map((col) => ({wch: COL_WIDTH[col.header] || DEFAULT_COL_WIDTH}))
  attachExcessCaveat(ws)

  const wb = XLSX.utils.book_new()
  // The data sheet is appended first so the file opens on it, not on the note.
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME)
  XLSX.utils.book_append_sheet(wb, buildNotesSheet(), NOTES_SHEET_NAME)
  return wb
}

// Sortable and filename-safe. Deliberately not the module's own fmtDate
// ("07 Aug 2026") — that has spaces in it, and a filename that sorts
// chronologically in a downloads folder is worth more here than one that
// reads like prose.
function stampDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  const p = (n) => String(n).padStart(2, '0')
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`
}

function slug(s) {
  return String(s || '')
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const RANGE_PRESET_LABEL = {'7d': 'last-7-days', '30d': 'last-30-days', '90d': 'last-90-days'}

/**
 * The date window, in the same two shapes state/query.js's inRange accepts:
 * a legacy preset string, or an explicit {startDate, endDate} with either
 * bound optionally open. "all-dates" when there is no window at all — never
 * an empty segment, so the filename never collapses to two underscores in a
 * row and a merchant can always see which window a file holds.
 */
export function rangeSlug(range) {
  if (!range || range === 'all') return 'all-dates'
  if (typeof range === 'string') return RANGE_PRESET_LABEL[range] || slug(range)
  const from = range.startDate ? stampDate(range.startDate) : null
  const to = range.endDate ? stampDate(range.endDate) : null
  if (from && to) return `${from}_to_${to}`
  if (from) return `from-${from}`
  if (to) return `upto-${to}`
  return 'all-dates'
}

/**
 * `Weight-discrepancies_<account>_<window>_<downloaded-at>.xlsx`.
 *
 * The account and the window are both in the name because a merchant
 * comparing two windows, or an ops user who has switched accounts, would
 * otherwise get two files called the same thing. The trailing minute stamp
 * is the last-resort tiebreak for two downloads of the SAME window — the
 * same `_dd-MMM-yyyy_HH-mm` idea appointments/shared/exportHelpers.js uses,
 * in this file's sortable spelling.
 */
export function disputeExportFilename(account, range, at) {
  const when = at instanceof Date ? at : new Date()
  const p = (n) => String(n).padStart(2, '0')
  const clock = `${stampDate(when)}-${p(when.getHours())}${p(when.getMinutes())}`
  return `Weight-discrepancies_${slug(account) || 'account'}_${rangeSlug(range)}_${clock}.xlsx`
}

/**
 * Writes the file. Returns `{status, count, filename}` — 'EMPTY' with no
 * file written when there is nothing matching, so the caller can say so out
 * loud rather than firing a download of a header row.
 */
export function downloadDisputeExport({rows, account, range} = {}) {
  const list = rows || []
  if (list.length === 0) return {status: 'EMPTY', count: 0, filename: null}
  const filename = disputeExportFilename(account, range)
  XLSX.writeFile(buildDisputeWorkbook(list), filename)
  return {status: 'OK', count: list.length, filename}
}
